// Public entry for the simulator. Mount it on a <canvas>, hand it a
// track (already validated via shared/track-schema's loadTrack), and
// it'll wire up the physics loop, controllers, and renderer.
//
// Returns a handle for the caller — the web app uses unmount() when
// the user navigates away from the race route, and resetCars() when
// they want to spawn back at the grid without reloading.
//
// Loop pattern: fixed 60 Hz physics, accumulator drains each rAF
// frame so a low-FPS render doesn't slow the sim. Render is decoupled
// — runs once per rAF regardless of how many physics ticks fired.
//
// Multi-car: each car carries its own derived telemetry, lapState,
// and controllerType. The tick loops over world.cars, picks the
// keyboard or AI controller per car, and advances physics + lap
// timing independently. Camera follows world.cars[selectedCarIdx].

import {
  createWorld, selectCar, toggleSelectedDriveMode,
  updateLineCache, updateRacingLineCache, anchorLineCacheAtRouteStart,
  activeLineFor,
} from './world.js';
import { forwardArcGap } from '../../shared/racing-line.js';
import { physicsStep } from './physics.js';
import { surfaceAt, applyWallCollision, centerlineArcAt, locationAtArc } from './track-queries.js';
import { applyCarCollisions } from './car-collisions.js';
import {
  attachKeyboard, detachKeyboard,
  createKeyboardController,
} from './controllers.js';
import { createAIController, DRIVE_MODES } from './ai-controller.js';
import { updateCondition, resetCondition } from './condition.js';
import { resetLapState, updateLapTiming } from './lap-timing.js';
import { render, invalidateRenderCache, preloadBgImage } from './render.js';
import { computeGridSlots } from './grid.js';
import { createRaceState, startRace as startRaceState, advanceRaceState, RACE_PHASES, RACE_MODES } from './race-state.js';
import { teamAIUpdate } from './team-ai.js';

const TICK_DT = 1 / 60;

export function mountSimulator(canvasEl, track, { spec, gridSize = 4, entries = null } = {}) {
  const ctx = canvasEl.getContext('2d');
  const world = createWorld(track, { spec, gridSize, entries });

  // Ready signal — resolves once the static-layer dependencies (bg
  // image today; future tile/sprite assets) are loaded. Hosts can
  // await this before revealing the canvas, to avoid a flash of
  // empty/black canvas while the bg image is decoding. The render
  // path consults the same cache, so by the time `ready` resolves
  // the next frame can blit without a second fetch.
  //
  // No bg image → resolves on the next animation frame (which is
  // also when the first render completes). 8 s cap so a broken
  // image doesn't strand the host forever.
  const bgUrl = track.backgroundImage?.dataUrl;
  const ready = bgUrl
    ? Promise.race([
        preloadBgImage(bgUrl),
        new Promise(r => setTimeout(r, 8000)),
      ]).then(() => {})
    : new Promise(r => requestAnimationFrame(() => r()));

  // Two controllers, picked per-car at tick time by car.controllerType.
  // Keyboard is module-scoped (only one keyboard exists); AI is a stub
  // until phase 3 — a per-car instance leaves room for per-car AI state
  // (target line offset, skill model) without re-plumbing later.
  const keyboardController = createKeyboardController();
  const aiControllers = world.cars.map(() => createAIController());

  const resize = () => {
    canvasEl.width = canvasEl.clientWidth || canvasEl.offsetWidth || window.innerWidth;
    canvasEl.height = canvasEl.clientHeight || canvasEl.offsetHeight || window.innerHeight;
    // Static-render cache is sized off the canvas; force a rebuild
    // next frame so the cache picks up the new viewport dimensions.
    invalidateRenderCache(world);
  };
  resize();
  window.addEventListener('resize', resize);

  const resetCars = () => spawnCarsOnGrid(world);
  resetCars();

  attachKeyboard(window, {
    onToggleDrive:  () => toggleSelectedDriveMode(world),
  });

  // Selection happens via the timing-and-scoring driver list in the
  // race page UI — clicking moving cars on the canvas was unworkable
  // at speed. The host calls back through the returned `selectCar`
  // helper.

  let lastTime = performance.now();
  let acc = 0;
  let rafId = 0;
  let alive = true;            // false after unmount
  let userPaused = false;      // user clicked the pause button
  let visibilityHidden = false; // tab is in the background
  let speedMultiplier = 2;     // 1× / 2× / 4× — fast-forward

  // Perf-metrics ring. Sliding window of the last PERF_WINDOW frames'
  // costs so the host's debug overlay can read smoothed numbers instead
  // of frame-jitter noise. Updated inline in the loop — overhead is a
  // pair of subtractions and an array slot write, sub-microsecond.
  const PERF_WINDOW = 60;
  const perfFrameMs  = new Float32Array(PERF_WINDOW);
  const perfTickMs   = new Float32Array(PERF_WINDOW);
  const perfRenderMs = new Float32Array(PERF_WINDOW);
  let perfIdx = 0;
  let perfFilled = 0;
  // Per-frame tick-time accumulator: a frame may run several physics
  // ticks (or zero, if paused / fast-forward starvation), so we sum
  // them and divide by the count to get "ms of tick work this frame".
  let perfTickAccumMs = 0;
  let perfTickCount   = 0;

  // Loop runs whenever the simulator is alive and the tab is visible.
  // `userPaused` only gates the physics block — render still fires
  // every frame so the user sees the world (grid pose, camera follow,
  // bg image cache filling in) while paused. Without this the canvas
  // sits at whatever was painted last, which on first mount is a
  // black flash before any pixels have been drawn.
  const isAlive = () => alive && !visibilityHidden;
  // Physics is gated only on PRE_RACE — that's the one phase where
  // we want a frozen grid (player hasn't clicked Start yet). RACING,
  // COOLDOWN, and FINISHED all keep ticking so the player can watch
  // cars driving / parking even after the results overlay appears.
  const isRacePhaseTicking = () => {
    return world.raceState?.phase !== RACE_PHASES.PRE_RACE;
  };
  const isTicking = () => isAlive() && !userPaused && isRacePhaseTicking();

  const startLoop = () => {
    if (rafId || !isAlive()) return;
    lastTime = performance.now();
    acc = 0;
    rafId = requestAnimationFrame(loop);
  };
  const stopLoop = () => {
    if (!rafId) return;
    cancelAnimationFrame(rafId);
    rafId = 0;
  };

  const loop = (now) => {
    rafId = 0;
    if (!isAlive()) return;
    const frameWallMs = now - lastTime;
    const realFrame = frameWallMs / 1000;
    lastTime = now;
    perfTickAccumMs = 0;
    perfTickCount   = 0;
    // Two pacing strategies:
    //
    //   1× — accumulator tracks wall-clock. A stuttered frame's
    //   missed ticks catch up on subsequent frames, so live races
    //   stay synced with real time even through small browser hitches.
    //   Burst-on-recovery is invisible at 1× because each tick is
    //   only a normal-frame's worth of motion.
    //
    //   >1× (fast-forward) — cap each frame's contribution at one
    //   physics tick (TICK_DT). The accumulator can never bank more
    //   than ~speedMultiplier ticks of work per frame, so the inner
    //   loop runs the same count every render and visual motion is
    //   uniform. Cost: a 30 ms stutter at 4× drops 50 ms of sim time
    //   that would otherwise have surged through the next render.
    //   Acceptable for FF since it's a spectator feature, not a
    //   timing-critical one — and a smooth 3.8× looks much better
    //   than a "4× with periodic teleports".
    const effFrame = speedMultiplier === 1
      ? Math.min(realFrame, 0.1)
      : Math.min(realFrame, TICK_DT);
    // Drain (don't accumulate) wall-clock time while paused — when
    // the user unpauses, we don't want the sim to suddenly burst-
    // catch-up the seconds spent watching the countdown.
    if (isTicking()) acc += effFrame * speedMultiplier;
    while (isTicking() && acc >= TICK_DT) {
      const tickStartMs = performance.now();
      // Controllers + physics + wall collision + lap timing. AI
      // controllers and traffic helpers read each car's racing-line
      // cache (car.derived.lineArc / lineSegIdx) — the cache reflects
      // the END of the PREVIOUS tick, which equals the START of this
      // tick because nothing moves the car between ticks.
      for (let i = 0; i < world.cars.length; i++) {
        const car = world.cars[i];
        const controller = car.controllerType === 'manual'
          ? keyboardController
          : aiControllers[i];
        car.lastInputs = controller(car, world, TICK_DT);
        car.derived.surface = surfaceAt(car.pos, world.track, world.geometry);
        // Condition first — burns fuel, wears tires, and resolves
        // car.effectiveSpec from spec + condition. Physics reads the
        // effective spec so the closed loop (worn tires → less grip,
        // empty tank → lighter car, damage → reduced engine) takes
        // hold this same tick. Damage is updated separately on impact
        // by applyImpactDamage in the collision routines.
        updateCondition(car, TICK_DT);
        physicsStep(car, car.lastInputs, car.effectiveSpec, car.derived, TICK_DT);
        applyWallCollision(car, world.track, world.geometry);
        // Lap timing is in sim-time (ms), not wall-clock. Keeps lap
        // clocks in sync with the physics: a tab-throttled or paused
        // sim doesn't inflate live lap times, and replays will produce
        // identical timing on playback.
        updateLapTiming(car.lapState, car, world.geometry.timingLines, world.simTime * 1000);
      }
      // Pairwise car-vs-car collision runs AFTER each car has stepped
      // its own physics + wall correction. Order matters: a wall hit
      // can change car position right before contact, and we want
      // those positions resolved before computing pair overlaps.
      applyCarCollisions(world.cars);
      // Refresh the line cache AFTER physics + lap timing so (lap, arc)
      // are written from the same end-of-tick state. Otherwise a tick
      // where a car crosses S/F leaves lapNumber incremented but
      // lineArc still pointing to the pre-crossing (near-totalLen)
      // arc, and the standings panel briefly reorders — the just-
      // crossed car appears at the top of its lap group for one tick
      // instead of immediately wrapping to arc ≈ 0. Visible at high
      // car counts and 4× FF as a flicker through the leaderboard.
      //
      // The cache is per-line: a pitting car projects onto the pit
      // route, a racing car onto the racing line. The racing-line
      // cache runs alongside as the canonical "where on the lap is
      // this car?" — used by standings + announcer regardless of
      // whether the AI is currently following pit route or racing
      // line.
      for (let i = 0; i < world.cars.length; i++) {
        const c = world.cars[i];
        updateLineCache(c, activeLineFor(c, world.geometry));
        updateRacingLineCache(c, world.geometry);
      }
      updatePitPhases(world);
      // Race-state phase machine. Advances after lap timing + line
      // caches are settled this tick, so leader / lap reads see the
      // up-to-date post-tick standings. Reads racingLineArc (just
      // refreshed above) to disambiguate leader at the same lap.
      advanceRaceState(world.raceState, world);
      // Track-context labels (corner / area) for the HUD + announcer
      // system. Updated on a coarse 10-tick cadence per car (~6 Hz at
      // 1×) — plenty for caption use, and the per-call cost is just
      // a centerline projection + linear walk over labels. Cars are
      // staggered across the cadence (modulo selectedCarIdx) so the
      // load stays even per frame instead of clumping every 10 ticks.
      const labels = world.geometry.labels;
      if (labels && labels.length > 0) {
        for (let i = 0; i < world.cars.length; i++) {
          const car = world.cars[i];
          if (car.derived._locationTicksLeft > 0) {
            car.derived._locationTicksLeft--;
            continue;
          }
          const arc = centerlineArcAt(car.pos, world.geometry);
          if (arc >= 0) {
            const loc = locationAtArc(arc, world.geometry);
            car.derived.currentCorner = loc.corner;
            car.derived.currentArea   = loc.area;
          }
          car.derived._locationTicksLeft = 10;
        }
      }
      // Team AI — strategic pit decisions for AI-managed cars. Runs
      // at 1 Hz (every 60 ticks) since pit calls are coarse decisions.
      // Only fires in solo mode: god mode means the player is the
      // strategist for every car, so the AI stays out of the way.
      // The player's own car is always exempt regardless of mode.
      world._teamAITicks = (world._teamAITicks ?? 0) + 1;
      if (world._teamAITicks >= 60) {
        world._teamAITicks = 0;
        const rs = world.raceState;
        if (rs?.raceMode === RACE_MODES.SOLO && (rs.phase === RACE_PHASES.RACING || rs.phase === RACE_PHASES.COOLDOWN)) {
          for (let i = 0; i < world.cars.length; i++) {
            if (i === rs.soloCarIdx) continue;
            teamAIUpdate(world.cars[i], world);
          }
        }
      }
      // Per-tick final-lap pit cancel for AI-managed cars. teamAIUpdate
      // covers this on its 1 Hz cadence, but pit-entry commits run at
      // 60 Hz — a request set on lap 19 could slip through to a lap-20
      // commit in the seconds between AI ticks. Cheap: only does work
      // once the leader actually reaches the final lap. Player's own
      // car is exempt so a manual last-lap PIT click still goes through
      // (it's a deliberate strategic choice, not an AI heuristic).
      {
        const rs = world.raceState;
        if (rs?.raceMode === RACE_MODES.SOLO
            && (rs.totalLaps ?? 0) > 0
            && (rs.phase === RACE_PHASES.RACING || rs.phase === RACE_PHASES.COOLDOWN)) {
          let leaderLap = 0;
          for (const c of world.cars) {
            const lap = c.lapState?.lapNumber ?? 0;
            if (lap > leaderLap) leaderLap = lap;
          }
          const finalLap = leaderLap >= rs.totalLaps;
          if (finalLap || rs.phase === RACE_PHASES.COOLDOWN) {
            for (let i = 0; i < world.cars.length; i++) {
              if (i === rs.soloCarIdx) continue;
              const ps = world.cars[i].pitState;
              if (ps && ps.phase === 'racing' && ps.requested) {
                ps.requested = false;
              }
            }
          }
        }
      }
      world.simTime += TICK_DT;
      acc -= TICK_DT;
      perfTickAccumMs += performance.now() - tickStartMs;
      perfTickCount++;
    }
    const renderStartMs = performance.now();
    render(ctx, world);
    const renderMs = performance.now() - renderStartMs;
    // Slot the frame's costs into the ring. perfTickCount===0 means
    // no ticks ran this frame (paused / pre-race / fast-forward
    // starvation); record 0 so the average reflects "tick work per
    // frame" honestly rather than skipping the slot.
    perfFrameMs[perfIdx]  = frameWallMs;
    perfTickMs[perfIdx]   = perfTickCount > 0 ? perfTickAccumMs / perfTickCount : 0;
    perfRenderMs[perfIdx] = renderMs;
    perfIdx = (perfIdx + 1) % PERF_WINDOW;
    if (perfFilled < PERF_WINDOW) perfFilled++;
    rafId = requestAnimationFrame(loop);
  };
  startLoop();

  // Auto-pause when the tab is hidden. Browsers throttle rAF in
  // background tabs (and fully suspend after a few minutes), so
  // without this the sim freezes mid-step while the user is away.
  // visibilityHidden is independent of userPaused so a manually-paused
  // race doesn't auto-resume when the tab comes back into focus.
  const onVisibilityChange = () => {
    visibilityHidden = document.hidden;
    if (visibilityHidden) stopLoop();
    else                  startLoop();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return {
    world,
    resetCars,
    selectCar: (idx) => selectCar(world, idx),
    // Pre-race → racing transition. Host calls this after the user
    // picks mode + driver + lap count on the overlay. Returns false
    // if the inputs are inconsistent (e.g. solo mode without a
    // driver pick) so the host UI can surface the error rather than
    // start a half-configured race.
    startRace(opts) {
      const ok = startRaceState(world.raceState, {
        raceMode:   opts?.raceMode,
        soloCarIdx: opts?.soloCarIdx,
        totalLaps:  opts?.totalLaps,
        simTime:    world.simTime,
      });
      if (ok) {
        // Capture each car's starting position at green flag. The
        // grid is laid out so cars[i] sits on slot[i] (pole = slot 0,
        // see spawnCarsOnGrid), so position is just i+1. Stamped here
        // — not at world creation — because resetCars during pre-race
        // could shuffle cars around the grid before the user actually
        // starts the race; we want the position they lined up on at
        // the lights, not at mount time. Read by the results overlay
        // to show positions gained / lost.
        for (let i = 0; i < world.cars.length; i++) {
          world.cars[i].startingPosition = i + 1;
        }
        // Reset the wall-clock anchor so any seconds spent on the
        // pre-race overlay don't bank up into the accumulator on
        // green flag — same reason as setPaused does it.
        lastTime = performance.now();
        startLoop();
      }
      return ok;
    },
    getRaceState() { return world.raceState; },
    // Wipe the in-progress race and stage a fresh pre-race grid using
    // the same setup (lap count + mode + solo driver pick) the user
    // already chose. Host re-shows the pre-race overlay; sim sits in
    // PRE_RACE again so physics is gated off until the user clicks
    // Start. Per-car race-end flags (_raceFinished / _finishedAt /
    // _recovered / startingPosition / etc.) get cleared here because
    // spawnCarsOnGrid only handles the in-race state — leaving them
    // would taint the next race's standings + results modal.
    restartRace() {
      const prev = world.raceState;
      const fresh = createRaceState();
      // Preserve the player's race configuration. createRaceState's
      // defaults are for first-mount; on a restart we want the same
      // race the user already started.
      fresh.totalLaps  = prev?.totalLaps  ?? fresh.totalLaps;
      fresh.raceMode   = prev?.raceMode   ?? fresh.raceMode;
      fresh.soloCarIdx = prev?.soloCarIdx ?? null;
      world.raceState = fresh;
      world.simTime = 0;
      world._teamAITicks = 0;
      for (const car of world.cars) {
        car._raceFinished       = false;
        car._finishLapAt        = null;
        car._finishedAt         = undefined;
        car._recovered          = false;
        car._cautionPaceTarget  = null;
        car._cautionPredIdx     = null;
        car._cautionRank        = null;
        car.startingPosition    = null;
        car.pitStops            = 0;
        car.lastInputs          = null;
      }
      resetCars();
      // Force the camera to re-anchor on the (possibly new) selected
      // car next frame and rebuild the static cache for the fresh
      // grid pose.
      invalidateRenderCache(world);
      // Drain wall-clock so the next loop iteration after the user
      // clicks Start doesn't burst-catch-up the seconds spent in the
      // pre-race overlay.
      lastTime = performance.now();
      acc = 0;
      // Loop runs in PRE_RACE for the render side (canvas reflects
      // the fresh grid); physics is naturally gated by isRacePhaseTicking.
      startLoop();
    },
    // Team-control surface. Phase 1 is just pit in / pit out — strategy
    // commands (push / pass / defend / conserve) get added here as they
    // come online so the host UI keeps a single import surface.
    requestPit(idx) {
      const car = world.cars[idx];
      if (!car || !world.geometry.pitRoute) return false;
      // Already on the pit route (driving down it OR stopped in the
      // box) — request is meaningless until they're back to racing.
      if (car.pitState.phase !== 'racing') return false;
      car.pitState.requested = true;
      return true;
    },
    cancelPit(idx) {
      const car = world.cars[idx];
      if (!car) return false;
      // Only cancel a not-yet-committed request. Once 'pitting' the
      // car is already on the pit route; bailing out mid-pit would
      // need a different (and trickier) rejoin path. Future work.
      if (car.pitState.phase !== 'racing') return false;
      car.pitState.requested = false;
      return true;
    },
    // Pit-order surface. setPitOrder merges flags into the car's
    // current order (so { repair: true } turns repair on without
    // touching tires/fuel). getPitOrder reads, getPitServiceSeconds
    // gives the live ETA — UI uses both for the order panel.
    setPitOrder(idx, partial) {
      const car = world.cars[idx];
      if (!car?.pitState?.order) return false;
      Object.assign(car.pitState.order, partial);
      return true;
    },
    getPitOrder(idx) {
      return world.cars[idx]?.pitState?.order ?? null;
    },
    getPitServiceSeconds(idx) {
      const car = world.cars[idx];
      if (!car) return 0;
      return computePitServiceSeconds(car);
    },
    // Drive-mode surface. setDriveMode validates against the table
    // in ai-controller.js so a typo doesn't silently fall back to
    // normal — the caller gets `false` back. getDriveMode reads.
    setDriveMode(idx, mode) {
      const car = world.cars[idx];
      if (!car) return false;
      if (!Object.prototype.hasOwnProperty.call(DRIVE_MODES, mode)) return false;
      car.driveMode = mode;
      return true;
    },
    getDriveMode(idx) {
      return world.cars[idx]?.driveMode ?? 'normal';
    },
    setPaused(p) {
      // Pause only halts physics ticks; the render loop keeps going
      // so the canvas reflects the current world state instead of
      // freezing on the last drawn frame (or, on initial mount,
      // staying black). startLoop() is a no-op if already running.
      userPaused = !!p;
      if (!userPaused) {
        // Reset lastTime so the wall-clock delta from the pause
        // window doesn't bank up into the accumulator on resume.
        lastTime = performance.now();
      }
      startLoop();
    },
    isPaused() { return userPaused; },
    ready,
    setSpeed(mul) {
      // Allowed range is intentionally narrow: 0.25× to 8×. Higher
      // multipliers risk physics-step starvation (>~30 ticks/frame
      // and the page chugs); lower than 0.25× is indistinguishable
      // from pause for most uses.
      speedMultiplier = Math.max(0.25, Math.min(8, mul));
    },
    // Smoothed perf metrics for the host's debug overlay. Returns
    // averages over the rolling window; FPS is derived from frame
    // wall-clock so it tracks render cadence (not tick cadence).
    // Returns null while the ring is empty (first frame).
    getPerfMetrics() {
      if (perfFilled === 0) return null;
      let frameSum = 0, tickSum = 0, renderSum = 0;
      for (let i = 0; i < perfFilled; i++) {
        frameSum  += perfFrameMs[i];
        tickSum   += perfTickMs[i];
        renderSum += perfRenderMs[i];
      }
      const avgFrameMs = frameSum / perfFilled;
      return {
        fps:         avgFrameMs > 0 ? 1000 / avgFrameMs : 0,
        avgFrameMs,
        avgTickMs:   tickSum   / perfFilled,
        avgRenderMs: renderSum / perfFilled,
        sampleCount: perfFilled,
      };
    },
    unmount() {
      alive = false;
      stopLoop();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      detachKeyboard();
    },
  };
}

// Distance (m) before the pit-entry-on-racing-line arc at which a
// "pit requested" car commits to actually pitting — i.e., flips its
// active line from racing to pit route. Cars respect the racing line
// up to and through the pit-entry crossing; only at the crossing does
// the active line "suddenly become" the pit lane spline. Earlier
// commit (the 60 m we previously used) puts the lookahead deep inside
// the entry ribbon — and the ribbon's first half is constrained to
// the trackline tangent so the target's lateral offset is small,
// pure-pursuit pulls gently, and the car drifts straight into a wall
// before reaching the pit lane. Committing AT the crossing puts the
// car at pit-route segment 0 with a target on the pit lane proper
// (via the entry-ribbon lookahead bias in ai-controller), giving a
// much wider angle and much stronger steering. 10 m is enough margin
// that even at 100 m/s the gap doesn't skip past the trigger window
// in a single tick (1.67 m/tick at 60 Hz). Speed approach is handled
// by the pre-commit pit-zone braking in the AI controller.
const PIT_COMMIT_TRIGGER_M = 25;

// Pit-stop service-time tuning. Total stop = base + max(tire, fuel)
// + repair, in sim-time seconds. Base covers the crew getting in
// position. Tire change and refueling happen in parallel (real
// motorsport). Damage repair is sequential — a longer task (nose
// change / aero panel swap) the crew has to do in series with the
// tire/fuel cycle. Numbers are tunable; the math lives in
// computePitServiceSeconds.
const PIT_BASE_STOP_S      = 2.0;        // car-stops-to-crew-ready
const PIT_TIRE_CHANGE_S    = 3.0;        // F1-fast tire change
const PIT_REFUEL_S_PER_KG  = 0.2;        // 0.2 s/kg → 10 s for a 50 kg fill
const PIT_REPAIR_S_PER_PCT = 0.3;        // 30 s for a 100 % rebuild

// Speed (m/s) below which we consider a boxStopped car "stopped" and
// start the service-window timer. 0.5 m/s ≈ 1.1 mph — the AI brakes
// to zero in ~1-2 s from pit-limit speed, so this triggers cleanly
// and avoids servicing-while-still-coasting.
const PIT_STOP_SPEED_THRESHOLD = 0.5;

// Sim-time seconds the next stop will take given the car's current
// condition + order. Pure read — safe to call from UI for live ETA.
// Order with no services selected still pays PIT_BASE_STOP_S as a
// drive-through floor.
//
// fuelKg / repairFrac are amount overrides. When null, fall back to
// "do the max" (fill tank, repair everything). The slider UI sets
// them explicitly so the ETA tracks the user's chosen amounts.
export function computePitServiceSeconds(car) {
  const order = car.pitState?.order;
  if (!order) return PIT_BASE_STOP_S;
  const cond  = car.condition;
  const cap   = car.spec.fuelCapacity ?? 0;
  const room  = Math.max(0, cap - (cond?.fuel ?? 0));
  const fuelDelta   = order.fuel
    ? Math.min(room, order.fuelKg != null ? Math.max(0, order.fuelKg) : room)
    : 0;
  const damage      = cond?.damage ?? 0;
  const repairFrac  = order.repairFrac != null ? Math.max(0, Math.min(1, order.repairFrac)) : 1;
  const damageDelta = order.repair ? damage * repairFrac * 100 : 0;
  const tireTime    = order.tires  ? PIT_TIRE_CHANGE_S         : 0;
  const fuelTime    = fuelDelta * PIT_REFUEL_S_PER_KG;
  const repairTime  = damageDelta * PIT_REPAIR_S_PER_PCT;
  return PIT_BASE_STOP_S + Math.max(tireTime, fuelTime) + repairTime;
}

// Apply the pit order to a car's condition. Called on the
// boxStopped → pitting transition (service complete). Tire change
// resets life AND temp — fresh tires come out cold, so the out-lap
// is genuinely slower until they warm back up. Refuel fills to cap.
// Repair zeroes damage.
function applyPitOrder(car) {
  const order = car.pitState?.order;
  const cond  = car.condition;
  if (!order || !cond) return;
  if (order.tires) {
    cond.tireLife      = 1.0;
    cond.tireTemp      = 30;  // matches INITIAL_TEMP_C in condition.js
                              // — fresh + cold rubber, requires a
                              // warmup lap to reach the optimal window.
    cond.tireZeroTimeS = 0;
    cond.blownTires    = false;
  }
  if (order.fuel) {
    const cap  = car.spec.fuelCapacity ?? cond.fuel;
    const room = Math.max(0, cap - cond.fuel);
    const add  = order.fuelKg != null ? Math.max(0, Math.min(room, order.fuelKg)) : room;
    cond.fuel  = cond.fuel + add;
  }
  if (order.repair) {
    const frac = order.repairFrac != null ? Math.max(0, Math.min(1, order.repairFrac)) : 1;
    cond.damage = Math.max(0, cond.damage * (1 - frac));
  }
}

// Run pit-phase transitions for every car. Called once per tick after
// the line cache refresh so we make decisions on end-of-tick state.
//
//   racing + requested → pitting    when racing-line arc is within
//                                    PIT_COMMIT_TRIGGER_M of the pit
//                                    entry (forward, wraps S/F).
//   pitting            → boxStopped when pit-route arc passes the
//                                    speed-zone midpoint (only once
//                                    per pit visit — `serviced` flag).
//   boxStopped         → pitting    after the service window: car
//                                    must have come to a stop AND
//                                    simTime ≥ serviceEndsAt.
//   pitting            → racing     when pit-route arc reaches the
//                                    end of the route (the car has
//                                    rejoined the main track).
//
// racing→pitting and pitting→racing transitions cold the line cache
// so the next updateLineCache does a full scan against the NEW line.
// boxStopped doesn't change the active line, so the cache stays warm.
function updatePitPhases(world) {
  const geom = world.geometry;
  if (!geom.pitRoute) return;            // no pit lane authored
  const racingLine = geom.racingLine;
  const entryArc   = geom.pitEntryRacingLineArc;
  const zone       = geom.pitSpeedZone;
  for (const car of world.cars) {
    const ps = car.pitState;
    if (!ps) continue;

    if (ps.phase === 'racing' && ps.requested
        && entryArc != null && racingLine
        && car.derived.lineSegIdx >= 0) {
      const gap = forwardArcGap(car.derived.lineArc, entryArc, racingLine.totalLength);
      if (gap > 0 && gap < PIT_COMMIT_TRIGGER_M) {
        ps.phase = 'pitting';
        ps.requested = false;
        ps.serviced = false;       // fresh visit; one service per visit
        ps.serviceEndsAt = null;
        // Anchor at the first locally-nearest segment of the pit
        // route walking forward from segment 0 — NOT a global scan.
        // See anchorLineCacheAtRouteStart for why: pit lanes that
        // run parallel to the racing line can sit close enough that
        // a global scan picks a mid-route segment, anchoring the AI
        // mid-pit on the very first tick of pitting.
        anchorLineCacheAtRouteStart(car, geom.pitRoute);
      }
    } else if (ps.phase === 'pitting'
               && car.derived.lineSegIdx >= 0) {
      const route = geom.pitRoute;
      const carArc = car.derived.lineArc;
      // pitting → boxStopped: stop at the assigned box's centerArc
      // (the AI's lateral peel-off has been pulling toward the box
      // over the last BOX_APPROACH_M, so by this arc the car is
      // already close to the box position). Cars without an
      // assignment fall back to the speed-zone midpoint — they still
      // get the lap-time cost, just no specific box.
      if (!ps.serviced) {
        let stopArc = null;
        if (car.pitBoxIdx != null && geom.pitBoxes?.boxes?.[car.pitBoxIdx]) {
          // box.stopArc = centerArc − buffer (set in track-geometry).
          // Same value the AI's back-prop targets; keeping both in
          // sync prevents "AI stops, then phase doesn't fire because
          // carArc < centerArc, so AI re-applies cruise throttle".
          stopArc = geom.pitBoxes.boxes[car.pitBoxIdx].stopArc;
        } else if (zone) {
          stopArc = (zone.startArc + zone.endArc) / 2;
        }
        if (stopArc != null && carArc >= stopArc) {
          ps.phase = 'boxStopped';
          ps.serviceEndsAt = null;   // set when car has actually stopped
          continue;
        }
      }
      // pitting → racing: past the last few meters of the route.
      // Margin guards against floating-point sticking on the last
      // segment after the windowed search clamps.
      if (carArc >= route.totalLength - 2) {
        ps.phase = 'racing';
        car.derived.lineSegIdx = -1;
      }
    } else if (ps.phase === 'boxStopped') {
      // Race-over short-circuit. A car that took the checkered and
      // pitted is parked for good — no service, no driving out. The
      // AI's boxStopped branch keeps the brake pinned so they sit
      // here cleanly. This is what the cool-down lap ends in.
      if (car._raceFinished) continue;
      // Wait for the car to come to a real stop before starting the
      // service timer. Without this gate the timer could start while
      // the car is still coasting and finish before it's even fully
      // braked — "fast pit stops during the brake" is a bug, not a
      // feature. The duration is computed from the current order +
      // condition (worn tires + low fuel + heavy damage = long stop).
      const speed = Math.hypot(car.vel.x, car.vel.y);
      if (ps.serviceEndsAt == null) {
        if (speed < PIT_STOP_SPEED_THRESHOLD) {
          ps.serviceEndsAt = world.simTime + computePitServiceSeconds(car);
        }
      } else if (world.simTime >= ps.serviceEndsAt) {
        // Service complete — apply the order (tires fresh-cold, tank
        // refilled, damage repaired per the order's flags). Then flip
        // back to pitting so the car drives out of the box. Incrementing
        // pitStops here (not on box entry / commit) keeps the count in
        // sync with "real stops" — a wave-off would advance commit but
        // never reach service complete and so wouldn't tick.
        applyPitOrder(car);
        car.pitStops = (car.pitStops ?? 0) + 1;
        // Stamp the lap a real stop completed on so the team-control
        // scouting view (rival cars in solo mode) can show "laps since
        // last pit" — the racing term is "stint length". Reading
        // lapNumber here matches the driver-was-stopped-on-this-lap
        // convention used elsewhere; the scout view subtracts to get
        // an age in laps.
        car._lastPitLap = car.lapState?.lapNumber ?? 0;
        ps.phase = 'pitting';
        ps.serviced = true;          // prevent re-entering boxStopped this visit
        ps.serviceEndsAt = null;
        // Line cache is still anchored on the pit route at midArc —
        // no cold-cache needed.
      }
    }
  }
}

// Place every car on its starting-grid slot. Slot positions come
// from computeGridSlots() — same function the renderer uses to draw
// the grid paint, so spawn placement and visual markings always
// match.
function spawnCarsOnGrid(world) {
  const { track, cars, camera, geometry } = world;
  const slots = computeGridSlots(track, geometry, cars.length);

  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    const slot = slots[i];
    car.pos = { x: slot.x, y: slot.y };
    car.heading = slot.heading;
    car.vel = { x: 0, y: 0 };
    car.angVel = 0;
    car.steer = 0;
    resetLapState(car.lapState);
    // Cold the line cache — the car teleported, so the cached anchor
    // segIdx is meaningless. The full-scan seeding below restores it
    // for the new spawn position.
    car.derived.lineSegIdx = -1;
    car.derived.racingLineSegIdx = -1;
    // Reset pit state on respawn — a half-completed pit-stop request
    // shouldn't survive a "back to grid" reset. Cars start every race
    // (and every reset) on the racing line. Order is preserved so a
    // user who set "tires only, no fuel" doesn't lose their setting
    // on respawn; reinitialize only if missing.
    const existingOrder = car.pitState?.order
      ?? { tires: true, fuel: true, repair: false };
    car.pitState = {
      phase: 'racing', requested: false, serviceEndsAt: null, serviced: false,
      order: existingOrder,
    };
    // Reset consumables — fresh tires, full tank, no damage. Mutates
    // in place to keep the same condition object across resets (the
    // HUD holds a reference and refreshing it via the same object is
    // cheaper than rebinding).
    resetCondition(car.condition, car.spec);
    // Clear stale label context — a respawn shouldn't keep showing
    // "Turn 12" until the next refresh cadence fires. Forcing
    // _locationTicksLeft to 0 also makes the next tick recompute.
    car.derived.currentCorner = null;
    car.derived.currentArea   = null;
    car.derived._locationTicksLeft = 0;
  }
  // Seed the line cache for every car at its new spawn position so
  // the first tick's AI controllers run with warm cache. (The main
  // loop now refreshes the cache at the END of each tick; without
  // this seed the very first tick after spawn would see lineSegIdx
  // === -1 and AI controllers would idle for a frame.)
  for (let i = 0; i < cars.length; i++) {
    updateLineCache(cars[i], activeLineFor(cars[i], world.geometry));
    updateRacingLineCache(cars[i], world.geometry);
  }

  // Camera snaps to the selected car so the reset doesn't pan.
  const sel = cars[world.selectedCarIdx] || cars[0];
  if (sel) {
    camera.x = sel.pos.x;
    camera.y = sel.pos.y;
  }
}
